import React from 'react'
import { useIntl } from 'react-intl'
import debounce from 'lodash.debounce'
import { css } from '@emotion/css'
import { Button } from '@contrast-security-inc/design-system-buttons'
import {
  Radio,
  SingleSelect
} from '@contrast-security-inc/design-system-inputs'
import { IconVersions } from '@contrast-security-inc/design-system-foundations'
import { Text } from '@contrast-security-inc/design-system-typography'
import { AgentSessionType, useAgentSessionsFilter } from '../api'
import { formatStyles, dateFormats } from '../../react-intl-helpers'
import {
  Modal,
  DialogBody,
  DialogContainer,
  DialogFooter,
  DialogHeader
} from '../../../legacy-design-system/modal'

interface FilterType {
  id: string
  name: string
}
interface MetadataType {
  label: string
  values: string[]
}
const getMetadata = (label, values): MetadataType[] =>
  label && values ? [{ label, values }] : []

const SessionMetadataUnconfigured = ({ appName }) => {
  const intl = useIntl()

  return (
    <DialogBody
      className={css`
        padding: 32px 80px;
      `}
    >
      <div
        className={css`
          align-items: center;
          display: grid;
          grid-row-gap: 16px;
          justify-items: center;
          text-align: center;
        `}
      >
        <IconVersions color="neutral50" size="lg" />
        <Text font="titleH3">
          {intl.formatMessage({
            id: 'title_sessionMetadataFilteringNotAvailable'
          })}
        </Text>
        <Text font="body">
          {intl.formatMessage(
            {
              id: 'detail_sessionMetadataConfigurationWarning'
            },
            {
              ...formatStyles.bold,
              appName
            }
          )}
          <a
            href={intl.formatMessage({
              id: 'link_configureSessionMetadata'
            })}
            target="_blank"
          >
            {intl.formatMessage({
              id: 'detail_sessionMetadataConfigurationLink'
            })}
          </a>
        </Text>
      </div>
    </DialogBody>
  )
}

const SessionMetadataDetails = ({ activeSession }) => {
  const intl = useIntl()
  const renderRow = (label, value, key = 0) => (
    <div
      key={key}
      className={css`
        display: flex;
      `}
    >
      <Text
        font="bodyStrong"
        className={css`
          flex: 0.35;
          text-align: right;
          padding-right: 16px;
          word-break: break-word;
        `}
      >
        {label}:
      </Text>
      <Text
        font="body"
        className={css`
          flex: 1;
        `}
      >
        {value || intl.formatMessage({ id: 'placeholder_na' })}
      </Text>
    </div>
  )

  return (
    <div
      className={css`
        display: flex;
        flex-direction: column;
        margin-top: 10px;
      `}
    >
      {renderRow(
        intl.formatMessage({ id: 'title_sessionCurrent' }),
        intl.formatDate(
          activeSession.createdDate * 1000,
          dateFormats.DATETIME_LONG
        )
      )}
      {activeSession.metadataSessions.map((it, index) =>
        renderRow(it.metadataField.displayLabel, it.value, index)
      )}
    </div>
  )
}

export const SessionMetadataModal = ({
  isOpen,
  onSubmit,
  onCancel,
  onClear,
  activeSession,
  appName,
  applicationUuid,
  organizationUuid,
  filterFields,
  isUsingMostRecentSession,
  isRemovableSession,
  hasCustomSession
}) => {
  // state
  const [isSessionsLoading, setIsSessionsLoading] = React.useState(false)
  const [metadata, setMetadata] = React.useState<MetadataType[]>([])
  const [isMostRecentSession, setIsMostRecentSession] = React.useState(
    isUsingMostRecentSession
  )
  const [systemProperty, setSystemProperty] = React.useState<
    FilterType | undefined
  >()
  const [agentSession, setAgentSession] = React.useState<
    AgentSessionType | undefined
  >()

  // hooks
  const intl = useIntl()
  const agentSessionsFilter = useAgentSessionsFilter({
    applicationUuid,
    organizationUuid,
    metadata
  })

  const sessionsSelectIsLoading = React.useMemo(
    () => isSessionsLoading || agentSessionsFilter.isLoading,
    [isSessionsLoading, agentSessionsFilter.isLoading]
  )

  const setMetadataEx = React.useCallback(
    (data) => {
      setIsSessionsLoading(false)
      setMetadata(data)
    },
    [setMetadata, setIsSessionsLoading]
  )

  // events
  const debouncedSetMetadata = React.useMemo(
    () => debounce(setMetadataEx, 500),
    [setMetadataEx]
  )

  return (
    <Modal isOpen={isOpen} onRequestClose={onCancel}>
      <DialogContainer size="medium" style={{ minWidth: '400px' }}>
        <DialogHeader
          onCancel={onCancel}
          headerContainerClassName={css`
            font-weight: bold;
          `}
        >
          <Text font="titleH5">
            {intl.formatMessage({ id: 'title_sessionMetadataFiltering' })}
          </Text>
        </DialogHeader>
        {!hasCustomSession ? (
          <SessionMetadataUnconfigured appName={appName} />
        ) : (
          <>
            <DialogBody>
              <div
                className={css`
                  display: flex;
                  flex-direction: column;
                `}
              >
                <Text font="body">
                  {intl.formatMessage(
                    {
                      id: activeSession
                        ? 'title_sessionMetadataFiltered'
                        : 'title_sessionMetadataSpecify'
                    },
                    {
                      ...formatStyles.bold,
                      appName
                    }
                  )}
                </Text>
                {activeSession ? (
                  <SessionMetadataDetails activeSession={activeSession} />
                ) : (
                  <>
                    <div
                      className={css`
                        display: flex;
                        margin: 24px 0 8px 0;
                        width: 350px;
                        justify-content: space-between;
                      `}
                    >
                      <Radio
                        id="most-recent-session-radio"
                        label={intl.formatMessage({
                          id: 'title_sessionMostRecent'
                        })}
                        isChecked={isMostRecentSession}
                        onChange={() => setIsMostRecentSession(true)}
                      />
                      <Radio
                        id="custom-session-radio"
                        label={intl.formatMessage({
                          id: 'title_sessionCustom'
                        })}
                        isChecked={!isMostRecentSession}
                        onChange={() => setIsMostRecentSession(false)}
                      />
                    </div>
                    {!isMostRecentSession && (
                      <>
                        <SingleSelect<FilterType>
                          id="system-property-single-select"
                          data-testid="system-property-single-select"
                          label={intl.formatMessage({
                            id: 'title_systemProperty'
                          })}
                          value={systemProperty}
                          itemToID={(item: FilterType) => item.id}
                          itemToString={(item: FilterType) =>
                            item ? item.name : ''
                          }
                          items={filterFields?.filter(
                            (it) => it.name !== 'Dissociated'
                          )}
                          onChange={(_, item: FilterType) => {
                            setSystemProperty(item)
                            if (item) {
                              setMetadataEx(getMetadata(item.name, ['']))
                            }

                            // agent session value depends on system property
                            // so clear it if we clear system property
                            if (!item) {
                              setAgentSession(undefined)
                            }
                          }}
                        />
                        <SingleSelect<AgentSessionType>
                          id="agent-session-single-select"
                          data-testid="agent-session-single-select"
                          label={intl.formatMessage({
                            id: 'placeholder_value'
                          })}
                          value={agentSession}
                          allowItemTextWrap={true}
                          isAsync={true}
                          isLoading={sessionsSelectIsLoading}
                          isDisabled={!systemProperty}
                          itemToID={(item: AgentSessionType) =>
                            item.agentSessionId
                          }
                          itemToString={(item: AgentSessionType) =>
                            !item
                              ? ''
                              : `${intl.formatDate(
                                  item?.createdDate * 1000,
                                  dateFormats.DATETIME_LONG
                                )} - ` +
                                item?.metadataSessions
                                  ?.map(
                                    (session) =>
                                      `${session.metadataField.displayLabel}: ${
                                        session.value ||
                                        intl.formatMessage({
                                          id: 'placeholder_na'
                                        })
                                      }`
                                  )
                                  .join(', ')
                          }
                          items={
                            sessionsSelectIsLoading
                              ? []
                              : agentSessionsFilter.data?.agentSessions
                          }
                          onChange={(_, item: AgentSessionType) =>
                            setAgentSession(item)
                          }
                          onInputChange={(inputValue: string) => {
                            setIsSessionsLoading(true)
                            debouncedSetMetadata(
                              getMetadata(systemProperty?.name, [inputValue])
                            )
                          }}
                        />
                      </>
                    )}
                  </>
                )}
              </div>
            </DialogBody>
            <DialogFooter>
              <Button
                data-testid="session-metadata-modal-remove-session"
                variant="secondary"
                isDisabled={!isRemovableSession}
                onClick={onClear}
              >
                {intl.formatMessage({ id: 'action_clearFilter' })}
              </Button>
              <Button
                data-testid="session-metadata-modal-submit"
                isDisabled={
                  (!isMostRecentSession &&
                    (!systemProperty || !agentSession)) ||
                  !!activeSession
                }
                onClick={() =>
                  onSubmit({
                    agentSession,
                    isMostRecentSession
                  })
                }
                className={css`
                  margin-left: 8px;
                `}
              >
                {intl.formatMessage({ id: 'action_applyFilter' })}
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContainer>
    </Modal>
  )
}
